'use client';

import {
  UPLOAD_PROMO_KEY,
  UPLOAD_PROMO_LABEL,
  useCancerAutocomplete,
} from '@/layers/presentation/hooks/useCancerAutocomplete';
import { PatientParam } from '@/types/models';
import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@nextui-org/react';
import { CloudUpload } from 'lucide-react';
import React from 'react';

interface CancerTypeAutocompleteProps {
  query: string;
  onQueryChange: (value: string) => void;
  patientParamsDictionary: PatientParam[];
  shouldShowUploadPromo?: boolean;
  onUploadPromo?: () => void;
  minQueryLength?: number;
  placeholder?: string;
  label?: string;
  className?: string;
  inputProps?: AutocompleteProps['inputProps'];
  allowsCustomValue?: boolean;
  allowsEmptyCollection?: boolean;
  isClearable?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  inputValue?: string;
  'data-testid'?: string;
  'aria-label'?: string;
  placeholderText?: string;
}

export const CancerTypeAutocomplete: React.FC<CancerTypeAutocompleteProps> = ({
  query,
  onQueryChange,
  patientParamsDictionary,
  shouldShowUploadPromo = false,
  onUploadPromo,
  minQueryLength = 2,
  placeholder,
  label,
  className = '',
  inputProps,
  allowsCustomValue = true,
  allowsEmptyCollection = true,
  isClearable = true,
  onFocus,
  onBlur,
  inputValue,
  placeholderText,
  ...rest
}) => {
  const { filteredOptions, handleInputChange, handleSelectionChange } = useCancerAutocomplete({
    query,
    onQueryChange,
    patientParamsDictionary,
    shouldShowUploadPromo,
    onUploadPromo,
    minQueryLength,
  });

  const AUTOCOMPLETE_PLACEHOLDER = placeholderText || 'Start typing to see suggestions';

  // Determine which options to show
  const displayOptions = filteredOptions.length > 0 ? filteredOptions : [AUTOCOMPLETE_PLACEHOLDER];

  // Merge default styling with custom inputProps
  const defaultInputProps = {
    classNames: {
      inputWrapper: ['bg-white', 'shadow-none', 'border-2', 'border-primary'],
      input: ['!text-foreground', '!placeholder:text-default-600', '!placeholder:opacity-80'],
      ...(inputProps?.classNames || {}),
    },
  };

  const mergedInputProps = inputProps
    ? {
        ...inputProps,
        classNames: {
          ...defaultInputProps.classNames,
          ...(inputProps.classNames || {}),
        },
      }
    : defaultInputProps;

  return (
    <Autocomplete
      type="text"
      label={label}
      placeholder={placeholder}
      className={className}
      classNames={{
        clearButton: 'text-default-700 hover:text-default-900',
        selectorButton: 'text-default-700 hover:text-default-900',
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      onFocus={onFocus}
      onBlur={onBlur}
      inputValue={inputValue}
      inputProps={mergedInputProps}
      allowsCustomValue={allowsCustomValue}
      allowsEmptyCollection={allowsEmptyCollection}
      isClearable={isClearable}
      listboxProps={{
        autoFocus: false,
      }}
      {...rest}
    >
      {[
        ...(shouldShowUploadPromo && onUploadPromo
          ? [
              <AutocompleteItem
                key={UPLOAD_PROMO_KEY}
                value={UPLOAD_PROMO_KEY}
                textValue={UPLOAD_PROMO_LABEL}
                aria-label={UPLOAD_PROMO_LABEL}
              >
                <div className="flex flex-row items-center gap-2">
                  <CloudUpload className="w-5 h-5" />
                  {UPLOAD_PROMO_LABEL}
                </div>
              </AutocompleteItem>,
            ]
          : []),
        ...displayOptions.map(option => (
          <AutocompleteItem
            key={option}
            value={option}
            textValue={option}
            isReadOnly={option === AUTOCOMPLETE_PLACEHOLDER}
          >
            {option}
          </AutocompleteItem>
        )),
      ]}
    </Autocomplete>
  );
};
